import type { Split } from 'type-fest'
import type { FormKitNode } from '@formkit/core'

/**
 * Order the date parts are shown in, per country. Display only — the BE always
 * receives an ISO date (see `encodeDate`).
 */
export const maskForCountry = (country: string) => {
  switch (country?.toLowerCase()) {
    case 'us':
    case 'ca':
      return 'month/day/year'
    case 'jp':
      return 'year/month/day'
    default:
      return 'day/month/year'
  }
}

export const useDateOfBirth = () => {
  const { country } = useRouteHelper()
  const { ts } = useI18n()

  const localizedMask = computed(() => maskForCountry(country))

  const maskConfig = computed(() =>
    localizedMask.value
      .split('/')
      .map(part => {
        switch (part) {
          case 'day':
            return `{##|placeholder:${ts('date.day').substring(0, 1)}}`
          case 'month':
            return `{##|placeholder:${ts('date.month').substring(0, 1)}}`
          case 'year':
            return `{####|placeholder:${ts('date.year').substring(0, 1)}}`
          default:
            return ''
        }
      })
      .join('/')
  )

  const dateOfBirthInputLabel = computed(() => {
    const dateLabel = ts('date.dateOfBirthLabel')
    const completeDate = ts(
      {
        'month/day/year': 'date.completeDateUS',
        'year/month/day': 'date.completeDateJP',
        'day/month/year': 'date.completeDate',
      }[localizedMask.value]
    )
    return `${dateLabel} ${completeDate}`
  })

  const validateDatePattern = (date: string, mask = localizedMask.value) => {
    const dateParts = date.split('/')

    if (dateParts.length !== 3) return false

    const datePartsNumber = dateParts.map(Number)

    if (datePartsNumber.some(part => Number.isNaN(part))) return false

    const maskParts = mask.split('/')

    const year = datePartsNumber[maskParts.indexOf('year')]
    const month = datePartsNumber[maskParts.indexOf('month')]
    const day = datePartsNumber[maskParts.indexOf('day')]

    if (year < 1900 || month < 1 || month > 12 || day < 1 || day > 31)
      return false

    // Verify it's a valid date by checking if the date object matches the input
    const dateObj = new Date(year, month - 1, day)
    return (
      dateObj.getFullYear() === year &&
      dateObj.getMonth() === month - 1 &&
      dateObj.getDate() === day
    )
  }

  const isValidDate = (date: FormKitNode) => {
    return validateDatePattern(date.value as string)
  }

  const MINIMUM_AGE = 18

  /**
   * Returns the latest birth date that still qualifies as MINIMUM_AGE: today at
   * midnight minus MINIMUM_AGE years; birth dates on or before it are valid.
   *
   * **ATTENTION: turning MINIMUM_AGE exactly today is allowed**
   */
  const getMinimumAgeCutoff = () => {
    const cutoff = new Date()
    cutoff.setHours(0, 0, 0, 0)
    cutoff.setFullYear(cutoff.getFullYear() - MINIMUM_AGE)
    return cutoff
  }

  const isBirthDateBeforeMinimumAge = (date: FormKitNode) => {
    const value = date.value as string
    if (!value) return false

    const datePartsNumber = value.split('/').map(Number)
    const maskParts = localizedMask.value.split('/')

    const birthDate = new Date(
      datePartsNumber[maskParts.indexOf('year')],
      datePartsNumber[maskParts.indexOf('month')] - 1,
      datePartsNumber[maskParts.indexOf('day')]
    )

    return birthDate <= getMinimumAgeCutoff()
  }

  const formatDate = (isoDate: string) => {
    const date = new Date(isoDate)
    const dateObj = {
      day: date.getUTCDate(),
      month: date.getUTCMonth() + 1,
      year: date.getUTCFullYear(),
    }
    type MaskConfigParts = Split<typeof localizedMask.value, '/'>
    const maskConfigParts = localizedMask.value.split('/') as MaskConfigParts
    return maskConfigParts
      .map(part => dateObj[part].toString().padStart(2, '0'))
      .join('/')
  }

  const encodeDate = (formattedDate: string, country: string) => {
    const encodingMask = maskForCountry(country)

    // Validate the input date format based on maskConfig
    const expectedFormat = new RegExp(
      `^${encodingMask
        .split('/')
        .map(part => (part === 'year' ? '\\d{4}' : '\\d{2}'))
        .join('\\/')}$`
    )

    if (!expectedFormat.test(formattedDate)) {
      throw new Error(`Invalid date format. Expected format: ${encodingMask}`)
    }

    if (!validateDatePattern(formattedDate, encodingMask)) {
      throw new Error('Date parts are out of valid range')
    }

    const maskConfigParts = encodingMask.split('/')

    const formattedDateParts = formattedDate.split('/').map(part => {
      const parsedPart = parseInt(part, 10)
      if (isNaN(parsedPart)) {
        throw new Error(`Invalid date part: ${part}`)
      }
      return parsedPart
    })

    const dateObj = maskConfigParts.reduce(
      (dateObj, part, i) => ({
        ...dateObj,
        [part]: formattedDateParts[i],
      }),
      {} as Record<string, number>
    )

    // Ensure all parts of the date are present
    if (
      dateObj.year === undefined ||
      dateObj.month === undefined ||
      dateObj.day === undefined
    ) {
      throw new Error('Incomplete date parts')
    }

    if (
      new Date(dateObj.year, dateObj.month - 1, dateObj.day) >
      getMinimumAgeCutoff()
    ) {
      throw new Error('Date of birth must meet the minimum age requirement')
    }

    // Construct the date object correctly
    const date = new Date(dateObj.year, dateObj.month - 1, dateObj.day, 12)
    return date.toISOString()
  }

  const lastCursorPosition = ref(0)

  const resetCursorPositionOnFocus = (e: FocusEvent) => {
    if (!e.target || (e.target as Node).nodeName !== 'INPUT') return
    const input = e.target as HTMLInputElement

    setTimeout(() => {
      const positionSelect = lastCursorPosition.value ?? 0
      input.setSelectionRange(positionSelect, positionSelect)
    }, 100)
  }

  const handleCursorPositionOnBlur = (e: FocusEvent) => {
    if (!e.target || (e.target as Node).nodeName !== 'INPUT') return
    const input = e.target as HTMLInputElement

    lastCursorPosition.value = input.selectionEnd ?? 0
  }

  return {
    maskConfig,
    dateOfBirthInputLabel,
    isBirthDateBeforeMinimumAge,
    isValidDate,
    formatDate,
    encodeDate,
    resetCursorPositionOnFocus,
    handleCursorPositionOnBlur,
  }
}
